"use client";

import { useSyncExternalStore } from "react";

const THEME_KEY = "nc-theme";
const THEME_EVENT = "nc-theme-changed";

type Theme = "light" | "dark" | "sepia" | "system";

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    return (localStorage.getItem(THEME_KEY) as Theme | null) || "light";
  } catch {
    return "light";
  }
}

function resolveThemeToVisual(stored: Theme): "light" | "dark" | "sepia" {
  if (stored === "system") {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return stored;
}

function subscribeAppTheme(onChange: () => void) {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_KEY || e.key === null) onChange();
  };
  const onLocal = () => onChange();
  window.addEventListener("storage", onStorage);
  window.addEventListener(THEME_EVENT, onLocal);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(THEME_EVENT, onLocal);
  };
}

function subscribeThemeAndSystem(onChange: () => void) {
  if (typeof window === "undefined") return () => {};
  const u1 = subscribeAppTheme(onChange);
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  mql.addEventListener("change", onChange);
  return () => {
    u1();
    mql.removeEventListener("change", onChange);
  };
}

/**
 * Resolves the stored `nc-theme` to a visual set for marketing screenshots.
 * "system" follows the OS; sepia and explicit light/dark are preserved.
 */
export function useResolvedMarketingTheme():
  "light" | "dark" | "sepia" {
  return useSyncExternalStore(
    subscribeThemeAndSystem,
    () => resolveThemeToVisual(readStoredTheme()),
    () => "light" as const
  );
}

const MOBILE_MQ = "(max-width: 767px)";

function readIsNarrow(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_MQ).matches;
}

function subscribeNarrow(onChange: () => void) {
  if (typeof window === "undefined") return () => {};
  const mql = window.matchMedia(MOBILE_MQ);
  const onResize = () => onChange();
  mql.addEventListener("change", onResize);
  window.addEventListener("resize", onResize);
  return () => {
    mql.removeEventListener("change", onResize);
    window.removeEventListener("resize", onResize);
  };
}

/** Matches &lt; md (768px) — when true, use marketing mobile images. */
export function useIsMarketingMobile(): boolean {
  return useSyncExternalStore(subscribeNarrow, readIsNarrow, () => false);
}
